import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { NotificationBell } from '../NotificationBell';
import {
    LayoutDashboard,
    Truck,
    Users,
    Wrench,
    Fuel,
    ClipboardCheck,
    GitCompare,
    History,
    BarChart3,
    AlertTriangle,
    Camera,
    Settings,
    LogOut,
    Shield,
    ChevronLeft,
    ChevronRight,
    Menu,
    Building2,
    Crown,
    Globe,
    Receipt,
    TrendingUp,
    Package,
    MapPin,
    ParkingCircle,
    Smartphone,
    QrCode,
    Lock,
    Warehouse,
    MessageSquare,
    Gauge,
    CalendarDays,
    Handshake,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import api from '../../lib/api';
import PWAInstallModal from '../pwa/PWAInstallModal';

// Super admin nav items - keys for translation
const superAdminNavItems = [
    { path: '/app/dashboard', icon: LayoutDashboard, labelKey: 'nav.overview', roles: ['super_admin'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/super-admin/companies', icon: Building2, labelKey: 'nav.companies', roles: ['super_admin'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/super-admin/users', icon: Users, labelKey: 'nav.allUsers', roles: ['super_admin'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/analytics', icon: BarChart3, labelKey: 'nav.analytics', roles: ['super_admin'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
];

// Regular nav items - keys for translation with edition support
// Personal Edition: Family use with up to 3 drivers and 3 vehicles, mobile-first
// Hybrid Edition: All features from Inspection + Carrier (mixed fleet - passenger + trucks)
const navItems = [
    { path: '/app/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard', roles: ['admin', 'fleet_manager', 'driver'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/check-in', icon: Camera, labelKey: 'nav.dailyCheckIn', roles: ['driver'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/vehicles', icon: Truck, labelKey: 'nav.vehicles', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/drivers', icon: Users, labelKey: 'nav.drivers', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },  // Personal: max 3 drivers (family)
    { path: '/app/live-fleet', icon: MapPin, labelKey: 'nav.liveFleet', roles: ['admin', 'fleet_manager'], editions: ['inspection', 'carrier', 'hybrid'] },  // NOT for personal
    { path: '/app/parking-map', icon: ParkingCircle, labelKey: 'nav.parkingMap', roles: ['admin', 'fleet_manager', 'driver'], editions: ['personal'] },  // Personal ONLY
    { path: '/app/maintenance', icon: Wrench, labelKey: 'nav.maintenance', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/fuel-logs', icon: Fuel, labelKey: 'nav.fuelLogs', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/inspections', icon: ClipboardCheck, labelKey: 'nav.inspections', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/comparison', icon: GitCompare, labelKey: 'nav.photoCompare', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/damage-reports', icon: AlertTriangle, labelKey: 'nav.damageReports', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/history', icon: History, labelKey: 'nav.history', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/analytics', icon: BarChart3, labelKey: 'nav.analytics', roles: ['admin', 'fleet_manager'], editions: ['inspection', 'carrier', 'hybrid'] },  // NOT for personal (simpler)
    { path: '/app/optimization', icon: TrendingUp, labelKey: 'nav.optimization', roles: ['admin', 'fleet_manager'], editions: ['carrier', 'hybrid'] },  // Carrier & Hybrid
    { path: '/app/capacity-analysis', icon: Package, labelKey: 'nav.capacityAnalysis', roles: ['admin', 'fleet_manager'], editions: ['carrier', 'hybrid'], locked: true },
    { path: '/app/billing', icon: Receipt, labelKey: 'nav.billing', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
    { path: '/app/users', icon: Shield, labelKey: 'nav.userManagement', roles: ['admin'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] },
];

// Lessee companies linked to a leasing company get a chat entry
const lesseeChatItem = { path: '/app/leasing-chat', icon: MessageSquare, labelKey: 'nav.chat', roles: ['admin', 'fleet_manager'], editions: ['personal', 'inspection', 'carrier', 'hybrid'] };

// Leasing edition has its own menu
const LEASING = ['leasing'];
const WORKSHOP = ['workshop'];
const workshopNavItems = [
    { path: '/app/workshop-jobs', icon: Wrench, labelKey: 'nav.workshopJobs', roles: ['admin', 'fleet_manager'], editions: WORKSHOP },
    { path: '/app/workshop-appointments', icon: CalendarDays, labelKey: 'nav.workshopAppointments', roles: ['admin', 'fleet_manager'], editions: WORKSHOP },
    { path: '/app/workshop-customers', icon: Users, labelKey: 'nav.workshopCustomers', roles: ['admin', 'fleet_manager'], editions: WORKSHOP },
    { path: '/app/workshop-partners', icon: Handshake, labelKey: 'nav.workshopPartners', roles: ['admin'], editions: WORKSHOP },
    { path: '/app/leasing-chat', icon: MessageSquare, labelKey: 'nav.chat', roles: ['admin', 'fleet_manager'], editions: WORKSHOP },
    { path: '/app/workshop-settings', icon: Gauge, labelKey: 'nav.workshopSettings', roles: ['admin'], editions: WORKSHOP },
];
const leasingNavItems = [
    { path: '/app/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard', roles: ['admin', 'fleet_manager'], editions: LEASING },
    { path: '/app/leasing-clients', icon: Users, labelKey: 'nav.lessees', roles: ['admin', 'fleet_manager'], editions: LEASING },
    { path: '/app/leasing-vehicles', icon: Truck, labelKey: 'nav.vehicles', roles: ['admin', 'fleet_manager'], editions: LEASING },
    { path: '/app/leasing-managers', icon: Shield, labelKey: 'nav.fleetManagers', roles: ['admin'], editions: LEASING },
    { path: '/app/leasing-maintenance', icon: Wrench, labelKey: 'nav.maintenance', roles: ['admin', 'fleet_manager'], editions: LEASING },
    { path: '/app/leasing-workshops', icon: Warehouse, labelKey: 'nav.workshops', roles: ['admin', 'fleet_manager'], editions: LEASING },
    { path: '/app/workshop-jobs', icon: Wrench, labelKey: 'nav.workshopJobs', roles: ['workshop'], editions: LEASING },
    { path: '/app/leasing-chat', icon: MessageSquare, labelKey: 'nav.chat', roles: ['admin', 'fleet_manager', 'workshop'], editions: LEASING },
    { path: '/app/workshop-settings', icon: Gauge, labelKey: 'nav.workshopSettings', roles: ['workshop'], editions: LEASING },
    { path: '/app/billing', icon: Receipt, labelKey: 'nav.billing', roles: ['admin'], editions: LEASING },
];

export const Sidebar = ({ isOpen, onToggle }) => {
    const { user, logout } = useAuth();
    const { language, toggleLanguage, t } = useLanguage();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const [companyEdition, setCompanyEdition] = useState(null); // Unknown until loaded
    const [hasLeasing, setHasLeasing] = useState(false);
    const [chatUnread, setChatUnread] = useState(0);
    const chatEnabled = ['leasing', 'workshop'].includes(companyEdition) || hasLeasing;

    useEffect(() => {
        if (!chatEnabled || !['admin', 'fleet_manager', 'workshop'].includes(user?.role)) return undefined;
        const poll = () => api.get('/leasing/chats/unread').then((r) => setChatUnread(r.data.unread || 0)).catch(() => {});
        poll();
        const t = setInterval(poll, 20000);
        return () => clearInterval(t);
    }, [chatEnabled, user?.role]);
    const [pwaModalOpen, setPwaModalOpen] = useState(false);

    // Fetch company edition on mount
    useEffect(() => {
        const fetchCompanyEdition = async () => {
            try {
                const response = await api.get('/auth/company');
                setCompanyEdition(response.data.edition || 'carrier');
                setHasLeasing(Boolean(response.data.leasing_company_id));
            } catch (error) {
                console.error('Error fetching company edition:', error);
                setCompanyEdition('carrier'); // Default fallback
            }
        };
        
        if (user && user.role !== 'super_admin') {
            fetchCompanyEdition();
        }
    }, [user]);

    // Use different nav items for super admin
    const isSuperAdmin = user?.role === 'super_admin';
    const itemsToFilter = isSuperAdmin ? superAdminNavItems : !companyEdition ? [] : companyEdition === 'leasing' ? leasingNavItems : companyEdition === 'workshop' ? workshopNavItems : hasLeasing ? [...navItems, lesseeChatItem] : navItems;
    
    // Filter by role AND edition
    const filteredNavItems = itemsToFilter.filter(item => 
        item.roles.includes(user?.role) && 
        (item.editions?.includes(companyEdition) || isSuperAdmin)
    );

    const getRoleBadge = (role) => {
        const badges = {
            super_admin: { bg: 'bg-purple-500/20', text: 'text-purple-400', labelKey: 'users.superAdmin' },
            admin: { bg: 'bg-red-500/20', text: 'text-red-400', labelKey: 'users.admin' },
            fleet_manager: { bg: 'bg-[#1B7EB5]/20', text: 'text-[#58A6B4]', labelKey: 'users.fleetManager' },
            driver: { bg: 'bg-green-500/20', text: 'text-green-400', labelKey: 'users.driver' },
            workshop: { bg: 'bg-amber-500/20', text: 'text-amber-500', labelKey: 'users.workshop' }
        };
        return badges[role] || badges.driver;
    };

    const badge = getRoleBadge(user?.role);

    return (
        <>
            {/* Mobile overlay */}
            {isOpen && (
                <div 
                    className="sidebar-overlay visible"
                    onClick={onToggle}
                />
            )}
            
            <aside 
                className={`sidebar ${isOpen ? 'open' : ''}`}
                style={{ width: collapsed ? '80px' : '260px' }}
            >
                {/* Header */}
                <div className="sidebar-header flex items-center justify-between">
                    {!collapsed && (
                        <div className="flex items-center">
                            <img 
                                src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp" 
                                alt="Cofleet Logo" 
                                style={{ width: '150px', height: 'auto' }}
                                className="object-contain"
                            />
                        </div>
                    )}
                    {collapsed && (
                        <img 
                            src="https://customer-assets-agu9un31.emergentagent.net/job_fleet-ops-62/artifacts/sxi4lya9_%CE%91%CE%BD%CF%89%CC%81%CE%BD%CF%85%CE%BC%CE%BF%20%CF%83%CF%87%CE%B5%CC%81%CE%B4%CE%B9%CE%BF.webp" 
                            alt="Cofleet Logo" 
                            style={{ width: '50px', height: '50px' }}
                            className="object-contain mx-auto"
                        />
                    )}
                    <div className="flex items-center gap-1">
                        {/* Notification Bell - only for admin and fleet_manager */}
                        {['admin', 'fleet_manager', 'workshop'].includes(user?.role) && !collapsed && (
                            <NotificationBell />
                        )}
                        <button 
                            onClick={() => setCollapsed(!collapsed)}
                            className="hidden lg:flex items-center justify-center w-8 h-8 rounded-sm hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                            data-testid="sidebar-collapse-btn"
                        >
                            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="sidebar-nav">
                    <div className="space-y-1">
                        {!collapsed && <p className="overline px-3 mb-2">{language === 'en' ? 'Navigation' : 'Πλοήγηση'}</p>}
                        {filteredNavItems.map((item) => {
                            const label = t(item.labelKey);
                            if (item.locked) {
                                const lockedHint = language === 'en' ? 'Locked service - not available yet' : 'Κλειδωμένη υπηρεσία - μη διαθέσιμη ακόμα';
                                return (
                                    <div
                                        key={item.path}
                                        className={`nav-item opacity-50 cursor-not-allowed select-none ${collapsed ? 'justify-center px-0' : ''}`}
                                        data-testid={`nav-${item.labelKey.split('.')[1]}-locked`}
                                        title={collapsed ? `${label} - ${lockedHint}` : lockedHint}
                                        aria-disabled="true"
                                    >
                                        <item.icon className="w-5 h-5 flex-shrink-0" />
                                        {!collapsed && <span className="nav-text flex-1">{label}</span>}
                                        {!collapsed && <Lock className="w-4 h-4 flex-shrink-0 ml-auto" data-testid="nav-locked-icon" />}
                                    </div>
                                );
                            }
                            return (
                                <NavLink
                                    key={item.path}
                                    to={item.path}
                                    className={({ isActive }) => 
                                        `nav-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`
                                    }
                                    data-testid={`nav-${item.labelKey.split('.')[1]}`}
                                    title={collapsed ? label : undefined}
                                >
                                    <item.icon className="w-5 h-5 flex-shrink-0" />
                                    {!collapsed && <span className="nav-text">{label}</span>}
                                    {item.path === '/app/leasing-chat' && chatUnread > 0 && (
                                        <span className="ml-auto text-[10px] bg-red-500 text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="nav-chat-unread">{chatUnread}</span>
                                    )}
                                </NavLink>
                            );
                        })}
                    </div>

                    <div className="mt-6">
                        {!collapsed && <p className="overline px-3 mb-2">{t('settings.title')}</p>}
                        <NavLink
                            to="/app/settings"
                            className={({ isActive }) => 
                                `nav-item ${isActive ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`
                            }
                            data-testid="nav-settings"
                            title={collapsed ? t('nav.settings') : undefined}
                        >
                            <Settings className="w-5 h-5 flex-shrink-0" />
                            {!collapsed && <span className="nav-text">{t('nav.settings')}</span>}
                        </NavLink>
                    </div>
                </nav>

                {/* Footer - User Info */}
                <div className="sidebar-footer">
                    {/* Language Toggle */}
                    <button
                        onClick={toggleLanguage}
                        className={`nav-item mb-3 w-full text-slate-600 hover:text-[#1B7EB5] hover:bg-[#1B7EB5]/10 ${collapsed ? 'justify-center px-0' : ''}`}
                        data-testid="language-toggle-btn"
                        title={collapsed ? (language === 'en' ? 'Switch to Greek' : 'Switch to English') : undefined}
                    >
                        <Globe className="w-5 h-5 flex-shrink-0" />
                        {!collapsed && (
                            <span className="flex items-center gap-2">
                                <span className={`font-medium ${language === 'en' ? 'text-[#1B7EB5]' : 'text-slate-400'}`}>EN</span>
                                <span className="text-slate-300">/</span>
                                <span className={`font-medium ${language === 'el' ? 'text-[#1B7EB5]' : 'text-slate-400'}`}>ΕΛ</span>
                            </span>
                        )}
                    </button>
                    
                    {/* PWA Install Button - Only for Admin/Fleet Manager */}
                    {(user?.role === 'admin' || user?.role === 'fleet_manager') && (
                        <button
                            onClick={() => setPwaModalOpen(true)}
                            className={`nav-item mb-3 w-full text-slate-600 hover:text-[#1B7EB5] hover:bg-[#1B7EB5]/10 ${collapsed ? 'justify-center px-0' : ''}`}
                            data-testid="pwa-install-btn"
                            title={collapsed ? (language === 'el' ? 'Εγκατάσταση App' : 'Install App') : undefined}
                        >
                            <QrCode className="w-5 h-5 flex-shrink-0" />
                            {!collapsed && (
                                <span>{language === 'el' ? 'QR Εγκατάστασης' : 'Install QR Code'}</span>
                            )}
                        </button>
                    )}
                    
                    {!collapsed ? (
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center text-slate-600">
                                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
                                <span className={`text-xs px-2 py-0.5 rounded-sm ${badge.bg} ${badge.text}`}>
                                    {t(badge.labelKey)}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center text-slate-600 mx-auto mb-3">
                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                    )}
                    <button
                        onClick={logout}
                        className={`nav-item text-red-500 hover:text-red-600 hover:bg-red-50 w-full ${collapsed ? 'justify-center px-0' : ''}`}
                        data-testid="logout-btn"
                        title={collapsed ? t('nav.logout') : undefined}
                    >
                        <LogOut className="w-5 h-5 flex-shrink-0" />
                        {!collapsed && <span>{t('nav.logout')}</span>}
                    </button>
                </div>
            </aside>
            
            {/* PWA Install Modal */}
            <PWAInstallModal 
                isOpen={pwaModalOpen} 
                onClose={() => setPwaModalOpen(false)} 
            />
        </>
    );
};

export const MobileMenuButton = ({ onClick }) => (
    <button 
        className="mobile-menu-btn"
        onClick={onClick}
        data-testid="mobile-menu-btn"
    >
        <Menu className="w-6 h-6" />
    </button>
);
